import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Settings as SettingsIcon, Mail, Plug, Network } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import HubQuickSearch from '../components/search/HubQuickSearch';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchExecutiveHub } from '../utils/hubSearch/searchSecondaryHubs';
import { HubSearchResult } from '../types/hubSearch';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import { connectGmail } from '../utils/gmailAuth';
import {
  ExecInboxThread, ExecInboxMailboxConfig, EXEC_INBOX_UNSORTED_STAGE_ID,
} from '../types';
import {
  subscribeExecThreads, subscribeMailboxConfig, syncAllExecutiveInbox,
  classifyExecThread, deleteExecThread, deleteExecThreadMirror, restoreExecThread, execThreadUndoSnapshot,
  getExecLabelMap, updateMailboxConfig, ensureExecGmailWatch,
  linkThread, acceptProposedLink, dismissProposedLink,
} from '../utils/execInboxApi';
import ExecInboxSidebar, { ExecView, MailboxOption, StageCount } from '../components/execInbox/ExecInboxSidebar';
import ExecInboxList from '../components/execInbox/ExecInboxList';
import ExecThreadPanel, { LinkCandidate } from '../components/execInbox/ExecThreadPanel';
import ExecSuiteSettings from '../components/execInbox/ExecSuiteSettings';
import DayBriefingPanel from '../components/execInbox/DayBriefingPanel';
import ExecutiveAssistantCalendarPage from './ExecutiveAssistantCalendarPage';
import {
  listAccessibleExecutives,
  profileBriefingTitle,
  type ExecutiveBriefingOption,
} from '../utils/executiveBriefingAccess';
import './OperationsHub.css';
import '../components/ops/OpsSidebar.css';
import '../components/execInbox/ExecInbox.css';
import '../components/pm/pm-shared.css';

function fmtSync(v: any): string {
  const d = v?.toDate ? v.toDate() : v instanceof Date ? v : null;
  return d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
}

const ExecutivePanel: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers, leads, enhancedDeals, timberProjects, projectTasks } = useCRM();
  useCRMCollections(['projectTasks']);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const myUid = user?.id || '';

  const [view, setView] = useState<ExecView>(() => {
    const v = searchParams.get('view');
    if (v === 'inbox' || v === 'calendar' || v === 'settings' || v === 'briefing') return v;
    return 'briefing';
  });
  const [selectedMailbox, setSelectedMailbox] = useState<string>(myUid);
  const [selectedExecutive, setSelectedExecutive] = useState<string>(myUid);
  const [cfg, setCfg] = useState<ExecInboxMailboxConfig | null>(null);
  const [ownerConnected, setOwnerConnected] = useState(false);
  const [threads, setThreads] = useState<ExecInboxThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState<string>('matt');
  const [openThreadId, setOpenThreadId] = useState<string | null>(() => searchParams.get('thread'));
  const [search, setSearch] = useState('');
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const [pendingMoves, setPendingMoves] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; error?: boolean } | null>(null);
  const [pendingUndo, setPendingUndo] = useState<{ thread: ExecInboxThread; snapshot: Record<string, unknown> } | null>(null);
  const undoTimerRef = useRef<number | null>(null);
  const autoSyncedRef = useRef<Set<string>>(new Set());

  const clearUndoTimer = () => {
    if (undoTimerRef.current != null) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  };

  useEffect(() => () => clearUndoTimer(), []);

  // Deep link: ?thread= opens inbox + thread panel.
  useEffect(() => {
    const threadId = searchParams.get('thread');
    if (threadId) {
      setView('inbox');
      setOpenThreadId(threadId);
    }
  }, [searchParams]);

  const handleSelectView = (v: ExecView) => {
    setView(v);
    if (v !== 'inbox') setOpenThreadId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'briefing') next.delete('view');
      else next.set('view', v);
      if (v !== 'inbox') next.delete('thread');
      return next;
    }, { replace: true });
  };

  const handleOpenInboxThread = (threadId: string) => {
    setView('inbox');
    setOpenThreadId(threadId);
    setSearchParams({ view: 'inbox', thread: threadId }, { replace: true });
  };

  useHubQuickSearchHotkey({
    onOpen: () => setShowQuickSearch(true),
    blocked: showQuickSearch || !!openThreadId,
  });

  const getExecutiveQuickSearchResults = useCallback(
    (query: string) => searchExecutiveHub(query, threads),
    [threads],
  );

  const handleExecutiveQuickSearchSelect = useCallback(
    (result: HubSearchResult) => {
      setView('inbox');
      setOpenThreadId(result.id);
      setSearchParams({ view: 'inbox', thread: result.id }, { replace: true });
    },
    [setSearchParams],
  );

  const showToast = (msg: string, error?: boolean) => {
    setToast({ msg, error });
    window.setTimeout(() => setToast(null), 2800);
  };

  const isAdmin = user?.role === 'admin';

  const executives: ExecutiveBriefingOption[] = useMemo(
    () => listAccessibleExecutives(crmUsers, myUid, isAdmin),
    [crmUsers, myUid, isAdmin],
  );

  const selectedExecutiveInfo = useMemo(
    () => executives.find(e => e.uid === selectedExecutive) || executives[0] || null,
    [executives, selectedExecutive],
  );

  const hasInboxSuite = useMemo(() => {
    const u = crmUsers.find(x => x.uid === selectedMailbox);
    return u?.execInbox?.enabled === true;
  }, [crmUsers, selectedMailbox]);

  const briefingOnlyMode = selectedExecutiveInfo?.briefingOnly === true;

  useEffect(() => {
    if (myUid && !selectedExecutive) setSelectedExecutive(myUid);
  }, [myUid, selectedExecutive]);

  useEffect(() => {
    if (briefingOnlyMode && view !== 'briefing') setView('briefing');
  }, [briefingOnlyMode, view]);

  // Default selected mailbox once we know the user.
  useEffect(() => { if (myUid && !selectedMailbox) setSelectedMailbox(myUid); }, [myUid, selectedMailbox]);

  // Mailboxes the current user can operate: their own + executives that delegated to them.
  const mailboxes: MailboxOption[] = useMemo(() => {
    const opts: MailboxOption[] = [];
    const me = crmUsers.find((u) => u.uid === myUid);
    opts.push({ uid: myUid, name: `${user?.name || me?.displayName || 'My mailbox'} (me)`, email: user?.email || me?.email || '' });
    crmUsers.forEach((u) => {
      if (u.uid === myUid) return;
      if (u.execInbox?.enabled && (u.execInbox.delegates || []).includes(myUid)) {
        opts.push({ uid: u.uid, name: u.displayName || u.email, email: u.email });
      }
    });
    return opts;
  }, [crmUsers, myUid, user?.name, user?.email]);

  const ownerInfo = useMemo(() => mailboxes.find((m) => m.uid === selectedMailbox) || mailboxes[0], [mailboxes, selectedMailbox]);
  const isOwner = selectedMailbox === myUid;
  const myConnected = useMemo(() => !!(crmUsers.find((u) => u.uid === myUid) as any)?.gmailConnection?.accessToken, [crmUsers, myUid]);

  // Subscribe to mailbox config (inbox suite only).
  useEffect(() => {
    if (!selectedMailbox || !hasInboxSuite) {
      setCfg(null);
      setOwnerConnected(false);
      return;
    }
    return subscribeMailboxConfig(selectedMailbox, (c, connected) => { setCfg(c); setOwnerConnected(connected); });
  }, [selectedMailbox, hasInboxSuite]);

  // Subscribe to threads (inbox suite only).
  useEffect(() => {
    if (!selectedMailbox || !hasInboxSuite) {
      setThreads([]);
      setThreadsLoading(false);
      return;
    }
    setThreadsLoading(true);
    return subscribeExecThreads(
      selectedMailbox,
      (list) => { setThreads(list); setThreadsLoading(false); },
      (e) => { showToast(e.message, true); setThreadsLoading(false); },
      2000,
    );
  }, [selectedMailbox, hasInboxSuite]);

  // Clear optimistic overrides once the mirror catches up.
  useEffect(() => {
    setPendingMoves((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      let changed = false;
      for (const t of threads) {
        if (next[t.threadId] && t.stage === next[t.threadId]) { delete next[t.threadId]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [threads]);

  // Ensure Gmail push watch when suite is enabled and Gmail is connected.
  useEffect(() => {
    if (!hasInboxSuite || !cfg?.enabled || !ownerConnected || !selectedMailbox) return;
    ensureExecGmailWatch(selectedMailbox).catch(() => undefined);
  }, [hasInboxSuite, cfg?.enabled, ownerConnected, selectedMailbox]);

  // Auto-sync once when an enabled, connected mailbox is empty.
  useEffect(() => {
    if (!hasInboxSuite || !cfg?.enabled || !ownerConnected || !selectedMailbox) return;
    if (autoSyncedRef.current.has(selectedMailbox)) return;
    if (threadsLoading) return;
    if (threads.length === 0) {
      autoSyncedRef.current.add(selectedMailbox);
      runInitialSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInboxSuite, cfg?.enabled, ownerConnected, selectedMailbox, threadsLoading, threads.length]);

  const effectiveThreads = useMemo(
    () => threads.map((t) => (pendingMoves[t.threadId] ? { ...t, stage: pendingMoves[t.threadId] } : t)),
    [threads, pendingMoves],
  );

  const counts: Record<string, StageCount> = useMemo(() => {
    const map: Record<string, StageCount> = {};
    for (const t of effectiveThreads) {
      const c = map[t.stage] || (map[t.stage] = { total: 0, unread: 0 });
      c.total++;
      if (t.unread) c.unread++;
    }
    return map;
  }, [effectiveThreads]);

  const visibleThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return effectiveThreads
      .filter((t) => t.stage === selectedStage)
      .filter((t) => !q || `${t.subject} ${t.lastFrom} ${t.lastFromName} ${t.snippet}`.toLowerCase().includes(q));
  }, [effectiveThreads, selectedStage, search]);

  const openThread = useMemo(
    () => effectiveThreads.find((t) => t.threadId === openThreadId) || null,
    [effectiveThreads, openThreadId],
  );

  const linkCandidates: LinkCandidate[] = useMemo(() => {
    const out: LinkCandidate[] = [];
    (leads || []).forEach((l: any) => out.push({ type: 'lead', id: l.id, name: l.name || l.fullName || l.email || 'Lead' }));
    (enhancedDeals || []).forEach((d: any) => out.push({ type: 'deal', id: d.id, name: d.name || d.title || d.dealName || 'Deal' }));
    (timberProjects || []).forEach((p: any) => out.push({ type: 'project', id: p.id, name: p.name || p.projectName || p.title || 'Project' }));
    return out;
  }, [leads, enhancedDeals, timberProjects]);

  const activeStageLabel = useMemo(() => {
    if (selectedStage === EXEC_INBOX_UNSORTED_STAGE_ID) return 'Unsorted';
    return cfg?.stages?.find((s) => s.id === selectedStage)?.label || selectedStage;
  }, [cfg?.stages, selectedStage]);

  async function runInitialSync() {
    try {
      const r = await syncAllExecutiveInbox(selectedMailbox, {
        maxResults: 100,
        onProgress: ({ totalSynced, page }) => {
          showToast(`Syncing… ${totalSynced} threads (page ${page})`);
        },
      });
      if (r.totalSynced > 0) {
        showToast(
          `Loaded ${r.totalSynced} thread${r.totalSynced === 1 ? '' : 's'}`
          + (r.hasMore ? ' (more remain)' : ''),
        );
      }
    } catch (e) {
      showToast((e as Error).message, true);
    }
  }

  async function handleDelete(thread: ExecInboxThread) {
    clearUndoTimer();
    setPendingUndo(null);
    if (openThreadId === thread.threadId) setOpenThreadId(null);
    try {
      try {
        await deleteExecThread(selectedMailbox, thread.threadId);
      } catch (e) {
        // Orphans: Gmail thread already gone, but Firestore mirror remains.
        if (!thread.id) throw e;
        await deleteExecThreadMirror(thread.id);
      }
      const snapshot = execThreadUndoSnapshot(thread);
      setPendingUndo({ thread, snapshot });
      undoTimerRef.current = window.setTimeout(() => {
        setPendingUndo(null);
        undoTimerRef.current = null;
      }, 5000);
    } catch (e) {
      showToast((e as Error).message, true);
    }
  }

  async function handleUndoDelete() {
    if (!pendingUndo) return;
    const { thread, snapshot } = pendingUndo;
    clearUndoTimer();
    setPendingUndo(null);
    try {
      await restoreExecThread(selectedMailbox, thread.threadId, snapshot);
      showToast('Conversation restored');
    } catch (e) {
      showToast((e as Error).message, true);
    }
  }

  async function handleMove(thread: ExecInboxThread, stageId: string) {
    setPendingMoves((prev) => ({ ...prev, [thread.threadId]: stageId }));
    try {
      await classifyExecThread(selectedMailbox, thread.threadId, stageId);
    } catch (e) {
      setPendingMoves((prev) => { const n = { ...prev }; delete n[thread.threadId]; return n; });
      showToast((e as Error).message, true);
    }
  }

  async function handleQuickLink(thread: ExecInboxThread, candidate: LinkCandidate | null) {
    try {
      await linkThread(selectedMailbox, thread.threadId, candidate);
      showToast(candidate ? `Linked to ${candidate.name}` : 'Link removed');
    } catch (e) {
      showToast((e as Error).message, true);
    }
  }

  async function handleAcceptLinkSuggestion(thread: ExecInboxThread) {
    if (!thread.proposedLink) return;
    try {
      await acceptProposedLink(selectedMailbox, thread.threadId, thread.proposedLink.target);
      showToast(`Linked to ${thread.proposedLink.target.name}`);
    } catch (e) {
      showToast((e as Error).message, true);
    }
  }

  async function handleDismissLinkSuggestion(thread: ExecInboxThread) {
    try {
      await dismissProposedLink(selectedMailbox, thread.threadId);
      showToast('Suggestion dismissed');
    } catch (e) {
      showToast((e as Error).message, true);
    }
  }

  async function handleEnable() {
    try {
      await updateMailboxConfig(selectedMailbox, { enabled: true });
      await getExecLabelMap(selectedMailbox);
      await ensureExecGmailWatch(selectedMailbox).catch(() => undefined);
      showToast('Executive Suite enabled — syncing…');
      await runInitialSync();
    } catch (e) {
      showToast((e as Error).message, true);
    }
  }

  const stages = cfg?.stages || [];

  const renderInbox = () => {
    if (!cfg?.enabled) {
      return (
        <div className="exec-empty">
          <Mail size={28} style={{ color: 'var(--pm-text-muted)' }} />
          <h3>Executive Suite is off for this mailbox</h3>
          <p>Turn on the email funnel to mirror Gmail labels into a clean, prioritized inbox the team and the agent can work from.</p>
          {isOwner
            ? <button className="exec-cta" onClick={handleEnable}>Enable Executive Suite</button>
            : <p>Ask the mailbox owner to enable it.</p>}
        </div>
      );
    }
    if (!ownerConnected) {
      return (
        <div className="exec-empty">
          <Plug size={28} style={{ color: 'var(--pm-text-muted)' }} />
          <h3>Connect Gmail to start the funnel</h3>
          <p>The funnel reads and writes Gmail labels for {ownerInfo?.email || 'this mailbox'}.</p>
          {isOwner
            ? <button className="exec-cta" onClick={() => connectGmail(myUid)}>Connect Google</button>
            : <p>The mailbox owner needs to connect their Google account.</p>}
        </div>
      );
    }
    return (
      <ExecInboxList
        threads={visibleThreads}
        stages={stages}
        selectedThreadId={openThreadId || undefined}
        loading={threadsLoading}
        totalMailboxThreads={effectiveThreads.length}
        ownerEmail={ownerInfo?.email || ''}
        ownerName={(ownerInfo?.name || '').replace(' (me)', '')}
        linkCandidates={linkCandidates}
        onOpen={(t) => setOpenThreadId(t.threadId)}
        onMove={handleMove}
        onDelete={handleDelete}
        onLink={handleQuickLink}
        onAcceptSuggestion={handleAcceptLinkSuggestion}
        onDismissSuggestion={handleDismissLinkSuggestion}
      />
    );
  };

  return (
    <div className="ops-hub exec-suite">
      <AppHeader />
      {executives.length === 0 ? (
        <div className="exec-suite-content" style={{ padding: 24 }}>
          <p>Executive briefing is not enabled for your account. Ask an admin to enable it in User Management.</p>
        </div>
      ) : (
      <div className="ops-hub-layout">
        <ExecInboxSidebar
          mailboxes={mailboxes}
          selectedMailbox={selectedMailbox}
          onSelectMailbox={(uid) => { setSelectedMailbox(uid); setOpenThreadId(null); }}
          executives={executives}
          selectedExecutive={selectedExecutive}
          onSelectExecutive={setSelectedExecutive}
          briefingOnly={briefingOnlyMode}
          view={view}
          onSelectView={handleSelectView}
          stages={stages}
          counts={counts}
          selectedStage={selectedStage}
          onSelectStage={setSelectedStage}
        />

        <div className="ops-hub-main">
          {view === 'calendar' ? (
            <ExecutiveAssistantCalendarPage embedded />
          ) : (
            <>
              <div className="ops-hub-toolbar">
                <div className="ops-hub-toolbar-left">
                  <h2 className="ops-hub-title">
                    {view === 'settings'
                      ? 'Executive Suite settings'
                      : view === 'briefing'
                        ? profileBriefingTitle(selectedExecutiveInfo?.profile || 'ceo')
                        : activeStageLabel}
                  </h2>
                  {view === 'inbox' && (
                    <div className="ops-hub-stats">
                      <span className="ops-stat">{visibleThreads.length} threads</span>
                      {cfg?.gmailWatch?.lastNotificationAt
                        ? <span className="ops-stat">live {fmtSync(cfg.gmailWatch.lastNotificationAt)}</span>
                        : cfg?.lastSyncAt && <span className="ops-stat">synced {fmtSync(cfg.lastSyncAt)}</span>}
                    </div>
                  )}
                </div>
                <div className="ops-hub-toolbar-right">
                  {view === 'inbox' && (
                    <>
                      <div className="exec-search">
                        <Search size={13} style={{ color: 'var(--pm-text-muted)' }} />
                        <input placeholder="Search this mailbox…" value={search} onChange={(e) => setSearch(e.target.value)} />
                      </div>
                    </>
                  )}
                  <button className="exec-icon-btn" onClick={() => navigate('/agent-harness')}>
                    <Network size={13} /> Agents
                  </button>
                  {!briefingOnlyMode && (
                    <button className="exec-icon-btn" onClick={() => handleSelectView('settings')}>
                      <SettingsIcon size={13} /> Settings
                    </button>
                  )}
                </div>
              </div>

              <div className="exec-suite-content">
                {view === 'settings'
                  ? (cfg && (
                      <ExecSuiteSettings
                        ownerUid={selectedMailbox}
                        ownerName={ownerInfo?.name || 'Executive'}
                        ownerEmail={ownerInfo?.email || ''}
                        cfg={cfg}
                        ownerConnected={ownerConnected}
                        currentUserUid={myUid}
                        currentUserConnected={isOwner ? ownerConnected : myConnected}
                        crmUsers={crmUsers}
                        onToast={showToast}
                      />
                    ))
                  : view === 'briefing'
                    ? (
                        <DayBriefingPanel
                          ownerUid={selectedExecutive}
                          ownerName={selectedExecutiveInfo?.name || ownerInfo?.name || 'Executive'}
                          profile={selectedExecutiveInfo?.profile || 'ceo'}
                          briefingOnly={briefingOnlyMode}
                          crmUsers={crmUsers}
                          timberProjects={timberProjects}
                          inboxThreads={effectiveThreads}
                          projectTasks={projectTasks}
                          onToast={showToast}
                          onOpenInboxThread={hasInboxSuite ? handleOpenInboxThread : undefined}
                          onOpenCalendar={hasInboxSuite ? () => handleSelectView('calendar') : undefined}
                        />
                      )
                    : renderInbox()}
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {openThread && cfg?.enabled && ownerConnected && (
        <ExecThreadPanel
          mailboxUserId={selectedMailbox}
          thread={openThread}
          stages={stages}
          ownerName={(ownerInfo?.name || '').replace(' (me)', '')}
          ownerEmail={ownerInfo?.email || ''}
          senderName={user?.name || 'you'}
          isOwner={isOwner}
          linkCandidates={linkCandidates}
          enhancedDeals={enhancedDeals}
          timberProjects={timberProjects}
          crmUsers={crmUsers}
          onClose={() => setOpenThreadId(null)}
          onDelete={() => openThread && handleDelete(openThread)}
          onMove={(stageId) => openThread && handleMove(openThread, stageId)}
          onToast={showToast}
        />
      )}

      {toast && <div className={`exec-toast${toast.error ? ' error' : ''}`}>{toast.msg}</div>}
      {pendingUndo && (
        <div className="exec-undo-toast" key={pendingUndo.thread.threadId}>
          <span className="exec-undo-msg">Conversation deleted</span>
          <button type="button" className="exec-undo-btn" onClick={handleUndoDelete}>Undo</button>
        </div>
      )}

      <HubQuickSearch
        open={showQuickSearch}
        onClose={() => setShowQuickSearch(false)}
        getResults={getExecutiveQuickSearchResults}
        onSelect={handleExecutiveQuickSearchSelect}
        placeholder="Search inbox threads…"
        ariaLabel="Executive inbox quick search"
        renderIcon={() => <Mail size={16} />}
      />
    </div>
  );
};

export default ExecutivePanel;
